"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, FileText, Link2, Search, Globe as Globe2, Smartphone, ChartLine as LineChart, Compass, Share2, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const localServices = [{
  id: 1,
  title: "Google Business Profile Optimization",
  category: "Map Pack Visibility",
  iconName: "MapPin",
  description: "Complete optimization of your Google Business Profile to dominate the local 3-Pack. I optimize categories, service areas, photos, products, and updates for maximum local conversion.",
  benefit: "High-intent calls & directions"
}, {
  id: 2,
  title: "Local Citation Building & Cleanup",
  category: "NAP Consistency",
  iconName: "Building2",
  description: "Ensuring consistent Name, Address, and Phone (NAP) details across authoritative UK and regional directories. I audit existing listings and clean up duplicates.",
  benefit: "Stronger search trust signals"
}, {
  id: 3,
  title: "Review Strategy & Reputation Management",
  category: "Social Proof",
  iconName: "Star",
  description: "Systematic review generation frameworks to collect authentic customer feedback, respond strategically, and build competitive review velocity that drives clicks.",
  benefit: "Higher click-through rates"
}, {
  id: 4,
  title: "Local Schema Markup & Structured Data",
  category: "Technical Local SEO",
  iconName: "Code2",
  description: "Custom JSON-LD schema implementation including LocalBusiness, GeoCoordinates, OpeningHours, and Service area markups to help Google parse your business entity.",
  benefit: "Rich snippet search features"
}, {
  id: 5,
  title: "Geo-Targeted Content & Location Pages",
  category: "Content Optimization",
  iconName: "FileText",
  description: "Tailored town, city, and regional service pages engineered for genuine local search intent without doorway pages or keyword stuffing.",
  benefit: "Increased qualified local leads"
}, {
  id: 6,
  title: "Hyper-Local Link Building & Regional PR",
  category: "Authority & Outreach",
  iconName: "Link2",
  description: "Acquiring contextual, high-quality links from local business associations, regional news portals, community sponsorships, and industry partners.",
  benefit: "Dominant regional authority"
}, {
  id: 7,
  title: "Local Keyword & Search Intent Research",
  category: "Strategic Targeting",
  iconName: "Search",
  description: "In-depth keyword discovery targeting geo-modified search phrases, high-commercial intent terms, and 'near me' queries relevant to your exact service areas.",
  benefit: "Targeted localized traffic"
}, {
  id: 8,
  title: "Multi-Location SEO Management",
  category: "Scalable Growth",
  iconName: "Globe2",
  description: "Coordinated local SEO strategies for businesses operating across multiple UK branches or locations, ensuring local relevance and consistent rankings.",
  benefit: "Multi-city footprint expansion"
}, {
  id: 9,
  title: "Voice Search & Mobile Local Optimization",
  category: "UX & Mobile First",
  iconName: "Smartphone",
  description: "Optimizing content for conversational natural language queries spoken via Siri, Google Assistant, and mobile devices with fast page load performance.",
  benefit: "Capture mobile 'on-the-go' searches"
}, {
  id: 10,
  title: "Local Competitor & Gap Analysis",
  category: "Market Intelligence",
  iconName: "LineChart",
  description: "Reverse-engineering top local map pack competitors to uncover their citation sources, review strategies, content depth, and backlink profiles.",
  benefit: "Actionable strategic roadmap"
}, {
  id: 11,
  title: "Local SEO Audits & Map Rank Tracking",
  category: "Performance Insights",
  iconName: "Compass",
  description: "Comprehensive baseline audits and localized geo-grid rank tracking. I measure real-world visibility street-by-street to refine strategy continuously.",
  benefit: "Transparent ROI reporting"
}, {
  id: 12,
  title: "Apple Maps & Alternative Map Engines",
  category: "Cross-Platform",
  iconName: "Share2",
  description: "Optimizing presence across Apple Maps, Bing Places, and automotive navigation platforms so prospective clients find you regardless of device.",
  benefit: "Total mobile map coverage"
}];
const iconMap: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  MapPin,
  Building2,
  Star,
  Code2,
  FileText,
  Link2,
  Search,
  Globe2,
  Smartphone,
  LineChart,
  Compass,
  Share2
};
export default function Localseoservices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground rounded-full">
            Local SEO Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-serif text-foreground mb-6">
            12 Specialized Local SEO Services Delivered Directly
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            When you hire YE Consultancy, you work directly with me — Yassine
            Essaadi. I personally execute each of these core local search deliverables
            to elevate your business into the Google 3-Pack and convert local searchers into loyal customers.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {localServices.map((service, i) => {
          const IconComponent = iconMap[service.iconName] || MapPin;
          return <motion.div key={service.id} variants={itemVariants} data-index={i} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border shadow-md bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-300 rounded-xl overflow-hidden group">
                  <div>
                    <div className="bg-primary text-primary-foreground p-6 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-lg bg-primary-foreground/10 text-primary-foreground backdrop-blur-xs">
                          <IconComponent className="size-6 shrink-0" />
                        </div>
                        <span className="text-xs font-medium tracking-wide text-primary-foreground/80 uppercase">
                          {service.category}
                        </span>
                      </div>
                      <Badge variant="outline" className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 text-[11.5px] px-2 py-0.5 rounded-full">
                        Service {i + 1}
                      </Badge>
                    </div>

                    <CardHeader className="pt-6 pb-2">
                      <CardTitle className="text-xl font-bold font-serif text-foreground group-hover:text-primary transition-colors duration-200">
                        {service.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-2">
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                        {service.description}
                      </CardDescription>

                      <div className="flex items-center gap-2 pt-2 text-xs font-medium text-foreground bg-muted/60 p-3 rounded-lg border border-border/50">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>Key outcome: {service.benefit}</span>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-4 pb-6 border-t border-border/40 mt-auto">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between hover:bg-secondary hover:text-secondary-foreground text-primary font-medium">
                      <Link to="/ContactPage">
                        <span>Inquire About This Service</span>
                        <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 md:mt-20 bg-muted/50 rounded-2xl p-8 md:p-12 border border-border text-center max-w-4xl mx-auto shadow-sm">
          <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground mb-4">
            Need a Tailored Local SEO Strategy for Your Market?
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8 text-base">
            Book a direct one-on-one consultation with me. We will review your Google Business Profile, local ranking bottlenecks, and build a custom action plan.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 py-6 font-semibold shadow-md">
              <Link to="/ContactPage">Book a Free Local SEO Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-lg px-8 py-6 font-semibold">
              <Link to="/CaseStudiesPage">View Verified Case Studies</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}